/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect} from 'react';
import {
  addMultiThemeEnabledObserver,
  addThemeChangeObserver,
  currentTheme,
  multiThemeEnabled
} from './ThemeStore';
import {DLSThemes} from './types';

/**
 * Resolves SASS theme changes to document-level data attributes for CSS
 * variable switching. Subscribes to theme and multi-theme state changes,
 * updating `document.documentElement.dataset.theme` accordingly.
 * 
 * This component should be added to an application's top-level `_app.tsx`
 * module:
 * 
 * ```typescript
 *  import SASSThemeResolver from '@ion/dls/src/theme/SASSThemeResolver';
 *  ...
 * 
 *  return (
 *    <>
 *      <PageViewportMeta />
 *      <FontLoader />
 *      <SASSThemeResolver />
 *      ...
 * ```
 *
 * When `multiThemeEnabled` is `false` (disabled), defaults to the `legacy`
 * theme (`dls/src/theme/legacy/theme.json5`).
 * 
 * When the theme state changes, the applications `<html>` element will be
 * updated to reflect the currently selected theme via the `data-theme` data
 * attribute. For example, the default (legacy) will initially be set as the
 * theme:
 * 
 * ```typescript
 *   <html lang="en" data-theme="legacy" ...>
 * ```
 * 
 * And when the selected theme is set to `current` (latest DLS), the attribute
 * will likewise be set to 'current':
 * 
 * ```typescript
 *   <html lang="en" data-theme="current" ...>
 * ```
 */
const SASSThemeResolver = () => {
  useEffect(() => {
    const updateTheme = () => {
      document.documentElement.dataset.theme = (
        multiThemeEnabled.value ? currentTheme.value : DLSThemes.legacy
      );
    };
    updateTheme();
    const unsubscribeTheme   = addThemeChangeObserver(updateTheme);
    const unsubscribeEnabled = addMultiThemeEnabledObserver(updateTheme);
    return () => {
      unsubscribeTheme();
      unsubscribeEnabled();
    };
  }, []);
  return null;
};

export default React.memo(SASSThemeResolver);
